﻿import { SketchControlPoint } from '.';
import { AnnotationNode } from './annotations';
import { IgnoreChanges } from './decorators/change-tracker';
import { Enumerable } from './decorators/enumerable';
import { Kb3dModel } from './decorators/kb3d-decorator';
import { instanceOf } from './decorators/model-utils';
import { eAxis, eBackground, eLoadingExperience, eRenderMode, eTextureSamplingMode } from './enums';
import { Connector } from './features/connector';
import { IImportInfo, ISceneNode } from './kb3d-models';
import { Kb3dObject } from './kb3d-object';
import { LightNode } from './lights';
import { MaterialNode } from './materials/material-node';
import { MateNode } from './mates/mate-node';
import { MeshGroupNode } from './meshes/mesh-group-node';
import { SpaceNode } from './meshes/space-node';
import { KbColor } from './primitives';
import { Token } from './token';
import { ViewpointNode } from './viewpoints/viewpoint-node';

@Kb3dModel({
    token: Token.SceneNode,
})
export class SceneNode extends MeshGroupNode implements ISceneNode {
    constructor() {
        super();
    }

    public nestedId: string;
    public background: eBackground;
    public environment: string;
    public environmentBlur: number;
    public environmentIntensity: number;
    public backgroundColor: KbColor;
    public ambientColor: KbColor;
    public renderMode: eRenderMode;
    public edgeColor: KbColor;
    public edgeWidth: number;
    public edgeDisplayTolerance: number;
    public viewpointId?: string;
    public frameViewpoint: boolean;
    public progressiveTextureLoad: boolean;
    public enableHoverEffects: boolean;
    public enableSceneOptimization: boolean;
    public enableDeferredSceneUpdates: boolean;
    public enableGrid?: boolean;
    public enableGridAxes?: boolean;
    public gridMajorInterval?: number;
    public gridNormal: eAxis;
    public gridSize?: number;
    public gridStepSize?: number;
    public connectorSize?: number;
    public arEnabled?: boolean;
    public fullscreenEnabled?: boolean;
    public xrAllowScaling: boolean;
    public unitsPerMeter: number;
    public xrAddShadow: boolean;
    public xrLightingEstimation: boolean;
    public enableFxaaPostProcess?: boolean;
    public enableLensEffectsPostProcess?: boolean;
    public enableSSAOPostProcess?: boolean;
    public environmentRotation: number;
    public dimensionUnitScalar?: number;
    public dimensionUnitSymbol?: string;
    public loadingExperience: eLoadingExperience;
    public loadingImagePath: string;
    public textureSamplingMode: eTextureSamplingMode;

    public _forceGrid = false;

    /** override base parent for more specific typing*/
    public _parent: SceneNode | undefined;

    @IgnoreChanges
    public conversionMap?: Record<string, any>;

    @IgnoreChanges
    public _configurator: any; //filled in by kbmax for easy retrieval of the associated configurator

    private _materials: MaterialNode[];
    @Enumerable()
    public get materials(): MaterialNode[] {
        if (!this._materials) this._materials = [];
        return this._materials;
    }
    public set materials(val) {
        this._materials = val;
    }

    private _lights: LightNode[];
    @Enumerable()
    public get lights(): LightNode[] {
        if (!this._lights) this._lights = [];
        return this._lights;
    }
    public set lights(val) {
        this._lights = val;
    }

    private _viewpoints: ViewpointNode[];
    @Enumerable()
    public get viewpoints(): ViewpointNode[] {
        if (!this._viewpoints) this._viewpoints = [];
        return this._viewpoints;
    }
    public set viewpoints(val) {
        this._viewpoints = val;
    }

    private _scenes: SceneNode[];
    @Enumerable()
    public get scenes(): SceneNode[] {
        if (!this._scenes) this._scenes = [];
        return this._scenes;
    }
    public set scenes(val) {
        this._scenes = val;
    }

    private _mates: MateNode[];
    @Enumerable()
    public get mates(): MateNode[] {
        if (!this._mates) this._mates = [];
        return this._mates;
    }
    public set mates(val) {
        this._mates = val;
    }

    private _annotations: AnnotationNode[];
    @Enumerable()
    public get annotations(): AnnotationNode[] {
        if (!this._annotations) this._annotations = [];
        return this._annotations;
    }
    public set annotations(val) {
        this._annotations = val;
    }

    private _importHistory: IImportInfo[];
    @Enumerable()
    public get importHistory(): IImportInfo[] {
        if (!this._importHistory) this._importHistory = [];
        return this._importHistory;
    }
    public set importHistory(val) {
        this._importHistory = val;
    }

    private _exportHistory: IImportInfo[];
    @Enumerable()
    public get exportHistory(): IImportInfo[] {
        if (!this._exportHistory) this._exportHistory = [];
        return this._importHistory;
    }
    public set exportHistory(val) {
        this._exportHistory = val;
    }

    public getChildren() {
        return [
            ...this.meshes,
            ...this.materials,
            ...this.lights,
            ...this.viewpoints,
            ...this.mates,
            ...this.connectors,
            ...this.annotations,
        ];
    }

    public getChildArrayForType(token: Token): Kb3dObject[] {
        if (token.isMeshOrGroup() || token.isLight()) {
            return this.meshes;
        } else if (token.isMaterial()) {
            return this.materials;
        } else if (token.isViewpoint()) {
            return this.viewpoints;
        } else if (token.isMate()) {
            return this.mates;
        } else if (token.isConnector()) {
            return this.connectors;
        } else if (token.isAnnotation()) {
            return this.annotations;
        } else {
            throw 'unexpected token';
        }
    }

    public getChildArray(node: Kb3dObject) {
        if (Token.withKey(node.$type).isLight()) {
            // Lights can be defined as meshes but can be in the light array as well, so we have to check there
            if (this.lights.contains(node as LightNode)) {
                return this.lights;
            }
        }
        return super.getChildArray(node);
    }

    public isNested() {
        return this._parent != null;
    }

    public runOnAllScenes(action: (sceneNode: SceneNode) => void) {
        action(this);
        for (const nested of this.scenes) {
            nested.runOnAllScenes(action);
        }
    }

    public getSpaceChildren(): SpaceNode[] {
        return this.meshes.concat(this.scenes);
    }

    public getNestedPathTo(node: Kb3dObject) {
        let path = node.id;
        let parent = node._parentScene;
        let isPath = false;
        while (parent) {
            if (parent === this) break;
            path = parent.nestedId + '/' + path;
            isPath = true;
            parent = parent._parent;
        }
        return `${isPath ? '@' : ''}${path}`;
    }

    public getAllConnectors() {
        const connectors = new Array<Connector>();
        this.runOnAllScenes(s => {
            connectors.push(...s._manager!.getByToken(Token.Connector).filter(c => c.enabled));
        });
        return connectors;
    }

    public getAllSketchControlPoints() {
        const points = new Array<SketchControlPoint>();
        this.runOnAllScenes(s => {
            points.push(...s._manager!.getByToken(Token.SketchControlPoint).filter(c => c.enabled));
        });
        return points;
    }
}

export function isSceneNode(node: Kb3dObject): node is SceneNode {
    return instanceOf(node, Token.SceneNode);
}
